import {
  Alert,
  Box,
  Button,
  Grid,
  Table,
  TrashIcon,
  Typography
} from "@dealer-center/core-components";
import { useOutletContext } from "react-router-dom";
import { AddDepartment, DeleteDepartment, EditDepartment } from "./modals";
import { useCallback, useEffect, useState } from "react";
import { useGetDepartments } from "@dealer-center/ddc-pages-departments-fetch";
import { hasDDCMembership } from "@dealer-center/ddc-pages-account-search-interface";
import { useGetUserPermissions } from "@dealer-center/ddc-pages-account-search-fetch";
import { useGetUser } from "@dealer-center/ddc-api-user-fetch";
import { useCC9Flags } from "@dealer-center/core-third-party-launch-darkly";
import { parseErrorMessages } from "./modals/utils";
import { defaultDepartments } from "./modals/defaultDepartments";

import type { AccountEntity } from "@dealer-center/ddc-api-account-interface";
import type { Department } from "@dealer-center/ddc-pages-departments-interface";

export interface AddDepartmentModal {
  modalToShow: "add";
}

export interface EditDepartmentModal {
  modalToShow: "edit";
  selected: Department;
}

export interface DeleteDepartmentModal {
  modalToShow: "delete";
  selected: Department;
}

export type ModalProps =
  | AddDepartmentModal
  | EditDepartmentModal
  | DeleteDepartmentModal;

export function DashboardDepartments() {
  const { accountEntity } = useOutletContext<{
    accountEntity: AccountEntity;
  }>();
  const {
    data = [],
    isLoading,
    isFetching,
    isRefetching,
    error,
    refetch
  } = useGetDepartments({
    accountId: accountEntity.id
  });
  const { data: userEntity } = useGetUser();
  const { data: permissions = [] } = useGetUserPermissions(
    !!userEntity?.internal
  );

  const { "deptsvc.department-management.enable": enableDepartmentCrud } =
    useCC9Flags();

  const hasPermission = hasDDCMembership(permissions);

  const [modalProps, setModalProps] = useState<ModalProps | null>();
  const [retryCount, setRetryCount] = useState(0);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const MAX_RETRIES = 3;

  useEffect(() => {
    if (error) {
      parseErrorMessages(
        error,
        "An unexpected error occurred. Please try again."
      ).then(setErrorMessages);
    } else {
      setErrorMessages([]);
    }
  }, [error]);

  const hideModal = useCallback(() => setModalProps(null), []);
  const isPreconfiguredDepartment = useCallback(
    (type: string) => defaultDepartments.some(d => d.value === type),
    [] // defaultDepartments is a module-level constant
  );

  // Show loading banner for background refetches
  const isRefetchingData = (isFetching || isRefetching) && !isLoading;

  if (error) {
    return (
      <Box sx={{ pt: 2 }}>
        <Alert type="error">
          <Box>
            <strong>Failed to load departments</strong>
            <Box
              component="ul"
              sx={{ mt: 1, mb: 2, fontSize: "0.875rem", pl: 2 }}
            >
              {errorMessages.map(msg => (
                <li key={msg}>{msg}</li>
              ))}
            </Box>
            <Button
              analyticsConfiguration={{
                eventName: "retry_load_departments"
              }}
              buttonStyle="primary"
              disabled={retryCount >= MAX_RETRIES}
              size="small"
              onClick={() => {
                setRetryCount(prev => prev + 1);
                refetch();
              }}
            >
              Retry {retryCount > 0 && `(${retryCount}/${MAX_RETRIES})`}
            </Button>
            {retryCount >= MAX_RETRIES && (
              <Typography sx={{ mt: 2, color: "error.main" }} variant="body-sm">
                Multiple retry attempts failed. Please contact support or try
                again later.
              </Typography>
            )}
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Grid container marginBlockEnd={2} spacing={2}>
      <Box sx={{ pt: 2, pb: 2, width: "100%" }}>
        {/* Show loading banner during background refetch */}
        {isRefetchingData && (
          <Alert sx={{ mb: 2 }} type="info">
            Refreshing departments...
          </Alert>
        )}
        <Table.Table
          enablePagination
          sortableColumns
          columns={[
            {
              title: "Label",
              dataIndex: "label"
            },
            {
              title: "Type",
              dataIndex: "type"
            },
            {
              title: "Last Modified",
              dataIndex: ["audit", "lastModified"],
              render: dateTime => new Date(dateTime).toLocaleString(),
              sorter: (a: Department, b: Department): number => {
                const aTime = a.audit?.lastModified
                  ? new Date(a.audit.lastModified).getTime()
                  : 0;
                const bTime = b.audit?.lastModified
                  ? new Date(b.audit.lastModified).getTime()
                  : 0;
                return aTime - bTime;
              }
            },
            {
              title: "Last Modified By",
              dataIndex: ["audit", "lastModifiedBy"]
            },
            {
              dataIndex: "type",
              width: "150px",
              align: "right",
              columnSortable: false,
              render: (_type, selected) => (
                <>
                  <Button
                    analyticsConfiguration={{
                      eventName: "edit_department"
                    }}
                    aria-label={`Edit ${selected.label} department`}
                    buttonStyle="secondary"
                    disabled={
                      !enableDepartmentCrud ||
                      isPreconfiguredDepartment(selected.type)
                    }
                    isLoading={isRefetchingData}
                    sx={{ mr: 2 }}
                    onClick={() =>
                      setModalProps({ modalToShow: "edit", selected })
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    analyticsConfiguration={{
                      eventName: "delete_department"
                    }}
                    aria-label={`Delete ${selected.label} department`}
                    buttonStyle="secondary"
                    disabled={!enableDepartmentCrud}
                    icon={<TrashIcon title="Delete" />}
                    isLoading={isRefetchingData}
                    onClick={() => {
                      setModalProps({ modalToShow: "delete", selected });
                    }}
                  />
                </>
              )
            }
          ]}
          data={data}
          dataDensity="small"
          defaultPageSize={10}
          isLoading={isLoading}
        />
        {hasPermission && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%"
            }}
          >
            <Button
              analyticsConfiguration={{
                eventName: "add_department"
              }}
              disabled={!enableDepartmentCrud}
              onClick={() => setModalProps({ modalToShow: "add" })}
            >
              Add Department
            </Button>
          </Box>
        )}
        {modalProps?.modalToShow === "delete" && (
          <DeleteDepartment hideModal={hideModal} {...modalProps} />
        )}
        {modalProps?.modalToShow === "edit" && (
          <EditDepartment
            hideModal={hideModal}
            {...modalProps}
            departments={data}
            userId={userEntity?.user}
          />
        )}
        {modalProps?.modalToShow === "add" && (
          <AddDepartment
            hideModal={hideModal}
            {...modalProps}
            accountId={accountEntity.id}
            departments={data}
            userId={userEntity?.user}
          />
        )}
      </Box>
    </Grid>
  );
}
